/**
 * Elective Course player — 6-week Read → Understand → Do sprint.
 * Reading weeks (mark complete), quiz weeks (auto-graded), and the Week-6 capstone
 * (AI-graded submission) that unlocks the badge + certificate on completion.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, BookOpen, Brain, Rocket, CheckCircle2, Lock, Award, Download, PartyPopper } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import ChessScenario from '../components/electives/ChessScenario';
import ChessRoster from '../components/electives/ChessRoster';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const PHASE_ICON = { 1: BookOpen, 2: Brain, 3: Rocket };

const ElectiveCourse = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWeek, setActiveWeek] = useState(1);
  const [busy, setBusy] = useState(false);
  const [scenarios, setScenarios] = useState({});
  // quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  // capstone state
  const [cap, setCap] = useState({ submission_type: 'link', url: '', reflection: '' });
  const [capResult, setCapResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/academy/electives/${courseId}`, { headers: authHeaders() });
      if (!r.data.access) { toast.error('Purchase this elective to begin'); navigate('/academy/elective-courses'); return; }
      setCourse(r.data);
      if (r.data.progress?.current_week) setActiveWeek(r.data.progress.current_week);
      // Load prior chess-scenario attempts (only for basketball_of_chess)
      if (r.data.route) { navigate(r.data.route, { replace: true }); return; }
      if (courseId === 'basketball_of_chess_v1') {
        try {
          const s = await api.get(`/academy/electives/${courseId}/scenarios/mine`, { headers: authHeaders() });
          setScenarios(s.data?.scenarios || {});
        } catch { /* ignore */ }
      }
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/academy/login'); return; }
      toast.error('Could not load course');
      navigate('/academy/elective-courses');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [courseId]);

  // start on mount if not started
  useEffect(() => {
    if (course && course.progress?.status === 'not_started') {
      api.post(`/academy/electives/${courseId}/start`, {}, { headers: authHeaders() }).catch(() => {});
    }
  }, [course, courseId]);

  const wp = course?.progress?.week_progress || {};
  const week = useMemo(() => (course?.weeks || []).find((w) => w.n === activeWeek && (w.reading || w.quiz)), [course, activeWeek]);
  const sideGamesWeek = useMemo(() => (course?.weeks || []).find((w) => w.n === activeWeek && w.side_games?.length > 0), [course, activeWeek]);

  const selectWeek = (n) => { setActiveWeek(n); setQuizAnswers({}); setQuizResult(null); setCapResult(null); };

  const markRead = async () => {
    setBusy(true);
    try {
      await api.post(`/academy/electives/${courseId}/week/${activeWeek}/read`, {}, { headers: authHeaders() });
      toast.success('Marked complete');
      await load();
      if (activeWeek < 6) selectWeek(activeWeek + 1);
    } catch (e) { toast.error('Failed'); } finally { setBusy(false); }
  };

  const submitQuiz = async () => {
    setBusy(true); setQuizResult(null);
    try {
      const r = await api.post(`/academy/electives/${courseId}/week/${activeWeek}/quiz`, { answers: quizAnswers }, { headers: authHeaders() });
      setQuizResult(r.data);
      if (r.data.passed) { toast.success(`Passed · ${r.data.score}%`); await load(); }
      else toast.error(`Scored ${r.data.score}% — need ${r.data.pass_mark}% to pass. Review & retry.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); } finally { setBusy(false); }
  };

  const submitCapstone = async () => {
    if (cap.reflection.trim().length < 40) { toast.error('Describe your capstone work in a few sentences first.'); return; }
    setBusy(true); setCapResult(null);
    try {
      const r = await api.post(`/academy/electives/${courseId}/capstone`, cap, { headers: authHeaders() });
      setCapResult(r.data);
      if (r.data.completed) toast.success('Capstone approved — course complete! 🎓');
      else toast.success('Capstone submitted for coach review.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); } finally { setBusy(false); }
  };

  const downloadCert = async () => {
    try {
      const r = await api.get(`/academy/electives/${courseId}/certificate.pdf`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `SAA_Certificate_${courseId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Certificate not ready'); }
  };

  if (loading || !course) return (<div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>);

  const accent = course.accent;
  const completed = course.progress?.status === 'completed';
  const capstone = week?.capstone;
  const unitLabel = courseId === 'basketball_of_chess_v1' ? 'Session' : 'Week';

  return (
    <div className="min-h-screen text-white pt-16" style={{ backgroundColor: '#050508' }} data-testid="elective-course-page">
      <div className="border-b-2 border-zinc-900 px-6 py-4" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="container mx-auto max-w-6xl">
          <button onClick={() => navigate('/academy/elective-courses')} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors" data-testid="elective-back"><ArrowLeft className="w-3.5 h-3.5" /> All Electives</button>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">{course.title}</h1>
              <p className="text-sm" style={{ color: accent }}>{course.tagline}</p>
            </div>
            {completed && (
              <button onClick={downloadCert} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="elective-download-cert"><Download className="w-4 h-4" /> Download Certificate</button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-8 grid md:grid-cols-[260px_1fr] gap-6">
        {/* Week nav */}
        <div className="space-y-1.5" data-testid="elective-week-nav">
          {(course.week_titles || course.weeks || []).filter((w, i, arr) => arr.findIndex((x) => x.n === w.n) === i).map((w) => {
            const PI = PHASE_ICON[w.phase] || BookOpen;
            const done = wp[String(w.n)]?.done;
            const active = w.n === activeWeek;
            return (
              <button key={w.n} onClick={() => selectWeek(w.n)} className={`w-full text-left border-2 px-3 py-2.5 transition-colors ${active ? 'border-orange-500 bg-orange-500/10' : 'border-zinc-800 hover:border-zinc-600'}`} data-testid={`elective-week-btn-${w.n}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-1"><PI className="w-3 h-3" /> {unitLabel} {w.n}</span>
                  {done ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : null}
                </div>
                <div className="text-xs font-bold text-white mt-1 leading-tight">{w.title}</div>
              </button>
            );
          })}
        </div>

        {/* Week content */}
        <div className="border-2 border-zinc-800 bg-zinc-950 p-6" data-testid="elective-week-content">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em]" style={{ color: accent }}>
            {(() => { const PI = PHASE_ICON[week.phase] || BookOpen; return <PI className="w-3.5 h-3.5" />; })()}
            {week.phase_label} · {unitLabel} {week.n}
          </div>
          <h2 className="text-xl md:text-2xl font-black text-white mt-1">{week.title}</h2>

          {/* Reading */}
          {week.reading?.length > 0 && (
            <div className="mt-4 space-y-3">
              {week.reading.map((p, i) => (<p key={i} className="text-sm text-zinc-300 leading-relaxed">{p}</p>))}
            </div>
          )}
          {/* Chess Roster Reveal — Basketball of Chess · Session 1 · Meet the Roster */}
          {courseId === 'basketball_of_chess_v1' && activeWeek === 1 && <ChessRoster />}
          {week.case_study && (
            <div className="mt-4 border-l-4 pl-4 py-2" style={{ borderColor: accent }} data-testid="elective-case-study">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Case Study</div>
              <div className="text-sm font-bold text-white mt-0.5">{week.case_study.title}</div>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{week.case_study.body}</p>
            </div>
          )}
          {week.key_terms?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {week.key_terms.map((t) => (<span key={t} className="text-[10px] font-bold uppercase tracking-wider border border-zinc-700 text-zinc-400 px-2 py-1">{t}</span>))}
            </div>
          )}
          {week.task && (
            <div className="mt-4 border-2 border-zinc-800 bg-black/40 p-4" data-testid="elective-task">
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>This Week&apos;s Rep</div>
              <p className="text-sm text-zinc-300 mt-1">{week.task}</p>
            </div>
          )}

          {/* Side-Games (Module A · Escape / Module B · Set) — sourced from sideGamesWeek */}
          {sideGamesWeek?.side_games?.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="elective-side-games">
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>Interactive Side-Games · Play & Apply</div>
              {courseId === 'basketball_of_chess_v1' ? (
                <div className="space-y-6">
                  {sideGamesWeek.side_games.map((sg) => (
                    <ChessScenario
                      key={sg.module}
                      courseId={courseId}
                      week={sideGamesWeek.n}
                      sideGame={sg}
                      previousAttempt={scenarios?.[`${sideGamesWeek.n}-${sg.module}`]}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sideGamesWeek.side_games.map((sg) => (
                    <div key={sg.module} className="border-2 p-4 bg-black/40 flex flex-col" style={{ borderColor: sg.kind === 'escape' ? '#3B82F6' : '#EF4444' }} data-testid={`sidegame-${sg.module}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] font-black tracking-[0.28em] uppercase px-2 py-0.5"
                              style={{ backgroundColor: sg.kind === 'escape' ? '#3B82F6' : '#EF4444', color: '#fff' }}>
                          Module {sg.module} · {sg.kind === 'escape' ? 'Escape' : 'Set'}
                        </span>
                      </div>
                      <div className="text-sm font-black text-white mt-1">{sg.title}</div>
                      <p className="text-xs text-zinc-400 mt-2 leading-relaxed flex-1">{sg.tactic}</p>
                      {sg.target_line?.length > 0 && (
                        <div className="mt-2 text-[10px] text-zinc-500 font-mono">
                          Target line: <span className="text-zinc-300">{sg.target_line.join(' → ')}</span>
                        </div>
                      )}
                      <a href={sg.lichess_url} target="_blank" rel="noopener noreferrer"
                         className="mt-3 inline-flex items-center justify-center gap-2 bg-white hover:bg-zinc-200 text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors"
                         data-testid={`sidegame-play-${sg.module}`}>
                        Play Scenario on Lichess ↗
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quiz */}
          {week.quiz?.length > 0 && (
            <div className="mt-6 space-y-5" data-testid="elective-quiz">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Understanding Check · {course.quiz_pass}% to pass</div>
              {week.quiz.map((q, idx) => {
                const res = quizResult?.results?.find((r) => r.id === q.id);
                return (
                  <div key={q.id} className={`border-2 p-4 ${res ? (res.correct ? 'border-emerald-500/50' : 'border-red-500/50') : 'border-zinc-800'}`} data-testid={`elective-q-${q.id}`}>
                    <p className="text-sm font-bold text-white">{idx + 1}. {q.prompt}</p>
                    {q.type === 'mc' ? (
                      <div className="mt-2 space-y-1.5">
                        {q.options.map((opt) => {
                          const letter = opt.trim()[0];
                          return (
                            <label key={opt} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                              <input type="radio" name={q.id} value={letter} checked={quizAnswers[q.id] === letter} onChange={() => setQuizAnswers((s) => ({ ...s, [q.id]: letter }))} disabled={!!quizResult} data-testid={`elective-q-${q.id}-opt-${letter}`} />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <input value={quizAnswers[q.id] || ''} onChange={(e) => setQuizAnswers((s) => ({ ...s, [q.id]: e.target.value }))} disabled={!!quizResult} placeholder="Your answer" className="mt-2 w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white focus:border-orange-500 outline-none" data-testid={`elective-q-${q.id}-input`} />
                    )}
                    {res && <p className={`text-xs mt-2 ${res.correct ? 'text-emerald-400' : 'text-red-400'}`}>{res.correct ? '✓ Correct' : `✗ Answer: ${res.answer}`} — {res.explanation}</p>}
                  </div>
                );
              })}
              {!quizResult ? (
                <button onClick={submitQuiz} disabled={busy} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="elective-quiz-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Quiz'}</button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-black ${quizResult.passed ? 'text-emerald-400' : 'text-red-400'}`}>{quizResult.passed ? `Passed · ${quizResult.score}%` : `Scored ${quizResult.score}%`}</span>
                  {!quizResult.passed && <button onClick={() => { setQuizResult(null); setQuizAnswers({}); }} className="text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="elective-quiz-retry">Try Again</button>}
                  {quizResult.passed && activeWeek < 6 && <button onClick={() => selectWeek(activeWeek + 1)} className="text-[10px] font-black uppercase tracking-widest text-orange-400 hover:text-orange-300" data-testid="elective-quiz-next">Next Week →</button>}
                </div>
              )}
            </div>
          )}

          {/* Capstone */}
          {capstone && (
            <div className="mt-6" data-testid="elective-capstone">
              <div className="border-2 p-5" style={{ borderColor: accent }}>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>Capstone Project</div>
                <h3 className="text-lg font-black text-white mt-1">{capstone.title}</h3>
                <p className="text-sm text-zinc-400 mt-1">{capstone.deliverable}</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Requirements</div>
                    <ul className="space-y-1">{capstone.requirements.map((r, i) => (<li key={i} className="text-xs text-zinc-300 flex gap-1.5"><span style={{ color: accent }}>•</span>{r}</li>))}</ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Grading Rubric</div>
                    <ul className="space-y-1">{capstone.rubric.map((r, i) => (<li key={i} className="text-xs text-zinc-400 flex gap-1.5"><span className="text-zinc-600">•</span>{r}</li>))}</ul>
                  </div>
                </div>
              </div>

              {completed || capResult?.completed ? (
                <div className="mt-5 border-2 border-emerald-500/50 bg-emerald-500/10 p-5 text-center" data-testid="elective-completed-panel">
                  <PartyPopper className="w-8 h-8 text-amber-400 mx-auto" />
                  <div className="text-lg font-black text-white mt-2">Course Complete!</div>
                  <div className="flex items-center justify-center gap-2 mt-2"><Award className="w-4 h-4 text-amber-400" /><span className="text-xs font-black uppercase tracking-widest text-amber-300">{course.badge.name}</span></div>
                  <div className="text-[11px] text-zinc-400 mt-1">+{course.credit_amount} {course.credit_type} Credit added to your transcript</div>
                  <button onClick={downloadCert} className="mt-4 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="elective-cert-btn-panel"><Download className="w-4 h-4" /> Download Certificate</button>
                </div>
              ) : capResult && !capResult.completed ? (
                <div className="mt-5 border-2 border-amber-500/50 bg-amber-500/10 p-4" data-testid="elective-pending-panel">
                  <div className="text-sm font-bold text-amber-300">Submitted · AI score {capResult.ai_score}%</div>
                  <p className="text-xs text-zinc-300 mt-1">{capResult.ai_feedback}</p>
                  <p className="text-[11px] text-zinc-500 mt-2">{capResult.message}</p>
                </div>
              ) : (
                <div className="mt-5 space-y-3" data-testid="elective-capstone-form">
                  <div className="grid sm:grid-cols-[160px_1fr] gap-2">
                    <select value={cap.submission_type} onChange={(e) => setCap((s) => ({ ...s, submission_type: e.target.value }))} className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm text-white focus:border-orange-500 outline-none" data-testid="elective-cap-type">
                      <option value="link">Link (Drive/YouTube)</option>
                      <option value="video">Video</option>
                      <option value="pdf">PDF</option>
                    </select>
                    <input value={cap.url} onChange={(e) => setCap((s) => ({ ...s, url: e.target.value }))} placeholder="Paste your submission link" className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm text-white focus:border-orange-500 outline-none" data-testid="elective-cap-url" />
                  </div>
                  <textarea value={cap.reflection} onChange={(e) => setCap((s) => ({ ...s, reflection: e.target.value }))} rows={5} placeholder="Describe your capstone work and how it meets each requirement. This is what your coach & the AI rubric grade." className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm text-white focus:border-orange-500 outline-none" data-testid="elective-cap-reflection" />
                  <button onClick={submitCapstone} disabled={busy} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 text-black px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="elective-cap-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Capstone'}</button>
                </div>
              )}
            </div>
          )}

          {/* Mark-read for reading/prep weeks */}
          {!week.quiz && !week.capstone && (
            <button onClick={markRead} disabled={busy} className="mt-6 flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="elective-mark-read">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : wp[String(week.n)]?.done ? <><CheckCircle2 className="w-4 h-4" /> Completed — Continue</> : 'Mark Complete & Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElectiveCourse;
